/* eslint-disable max-lines-per-function */
import React, {useEffect} from 'react'
import {Lang} from 'yougile-lang'
import {Hub, noBoardContainerId} from 'yougile-model'
import {
  addingAllowed,
  archiveAllowed,
  deleteAllowed,
  moveFromAllowedByPermissions,
  renameAllowed
} from '@client/hub-actions-allowed'
import {MovementClient} from '@client/movement-client'
import {PermissionsChecker} from '@client/permissions-checker'
import {Backend} from '@common/backend'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {boardSettingsStore} from '@components/board/board-settings/board-settings-store'
import {Menu, MenuItemData} from '@components/common/base/menu'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {getAllTasksFromPrivateLists} from '@components/my-tasks/utils'
import {TaskColorPicker} from '@components/task-color-picker/task-color-picker'
import {BooleanHookState} from '@containers/hooks'
import {Column} from '@extensions/column'
import {createMirroredTask} from '@extensions/mirrored-tasks/create-mirrored-task'
import {isMirroredHub} from '@extensions/mirrored-tasks/is-mirrored-hub'
import {SubtaskUtil} from '@extensions/subtask-util'
import {Task} from '@extensions/task'
import {getTaskBoard} from '@extensions/task-data-helper'
import {useDnDStore} from '../draggable/dnd-store'
import {moveTemplateApply} from './move-template-apply'
import {Props} from './task-menu-new'
import {InnerProps} from './task-menu-new-base'
import {useTaskCover} from './use-task-cover'

type TaskMenuNewDropdownProps = Props &
  InnerProps & {
    reference: React.RefObject<Element> | Element | null
    closeMenu: () => void
    moveOpen: BooleanHookState
  }

// eslint-disable-next-line max-lines-per-function
export const TaskMenuNewDropdown = (props: TaskMenuNewDropdownProps) => {
  const {reference, closeMenu, moveOpen, canSelectMultiple = true} = props
  const [dndState] = useDnDStore()
  useEffect(() => {
    dndState.dragStarted && closeMenu()
  }, [dndState.dragStarted])

  const {
    task,
    toggleComplete,
    deleteTask,
    toggleArchive,
    rename,
    removeFromSubtasks,
    addSubtask,
    selectMultipleTasksToggleOn,
    hub,
    isParentTaskTemplate,
    asSubtask,
    visibleItems
  } = props

  const createTaskMirrorHandler = () => {
    void createMirroredTask(props.hub, props.hub.location)
  }

  const canComplete =
    PermissionsChecker.checkComplete(hub) &&
    (!PermissionsChecker.shouldFallbackToOldCheckComplete(hub) ||
      PermissionsChecker.oldCheckComplete(hub))
  const canRename = !isParentTaskTemplate && renameAllowed(task.id)
  const canMove =
    !isParentTaskTemplate &&
    moveFromAllowedByPermissions(task.id, task.location)

  const removeFromBoard = props.removeFromBoard && deleteAllowed(task.id)
  const duplicate = props.duplicate && addingAllowed(task.location)

  const makePublic = () => {
    delete hub.data.private
    hub.commit()
  }

  const board = getTaskBoard(hub)

  const isWithoutBoard = task.location === noBoardContainerId
  const copyLink = () => Task.copyLink(task)
  const {uploadCover, removeCover} = useTaskCover(hub, 'right-panel')

  const menuItems: MenuItemData[] = []

  const isAddSubtaskVisible =
    !visibleItems || visibleItems.includes('add-subtask')
  if (isAddSubtaskVisible && addSubtask && !isParentTaskTemplate) {
    menuItems.push({
      id: 'add-subtask',
      icon: 'IconRowInsertBottom',
      title: Lang.msg.subtask.createSubtask,
      onClick: addSubtask
    })
  }

  const isMirroredVisible =
    !visibleItems || visibleItems.includes('create-task-mirror')
  if (
    isMirroredVisible &&
    Backend.features.mirroredTasks &&
    task &&
    !isWithoutBoard &&
    !asSubtask &&
    addingAllowed(task.location)
  ) {
    menuItems.push({
      id: 'create-task-mirror',
      icon: 'IconCustomMirroredTask',
      title: Lang.msg.mirroredTasks.taskCardMenuItem,
      onClick: createTaskMirrorHandler
    })
  }

  const isCompleteVisible =
    !visibleItems || visibleItems.includes('complete-task')
  if (isCompleteVisible && !isParentTaskTemplate) {
    menuItems.push({
      id: 'complete-task',
      icon: task.completedAt > 0 ? 'IconCircleCheckFilled' : 'IconCircleCheck',
      iconColor: task.completedAt > 0 ? 'success' : undefined,
      title:
        task.completedAt > 0 ? Lang.msg.menuUncomplete : Lang.msg.menuComplete,
      onClick: toggleComplete,
      disabled: !canComplete
    })
  }
  const isRenameVisible = !visibleItems || visibleItems.includes('rename-task')
  isRenameVisible &&
    menuItems.push({
      id: 'rename-task',
      icon: 'IconEdit',
      title: Lang.msg.rename,
      onClick: rename,
      disabled: !rename || !canRename
    })

  if (!isParentTaskTemplate) {
    const isMakePublicVisible =
      !visibleItems || visibleItems.includes('make-public')
    const parents = SubtaskUtil.getParentTaskIds(task.id)
    const allTasksFromPrivateLists = getAllTasksFromPrivateLists()
    const isFromPrivateList = !parents.length
      ? allTasksFromPrivateLists.includes(task?.id)
      : parents.some(parentId => allTasksFromPrivateLists.includes(parentId))
    if (isMakePublicVisible && task.isPrivate && !isFromPrivateList) {
      menuItems.push({
        id: 'make-public',
        icon: 'IconLockOff',
        title: Lang.msg.chat.makePublic,
        onClick: makePublic
      })
    }

    const isMovementVisible =
      !visibleItems || visibleItems.includes('move-task')
    if (isMovementVisible && !task.isPrivate) {
      addMovement({
        menuItems,
        moveOpen,
        isWithoutBoard,
        task: hub,
        board,
        canMove
      })
    }
  }

  if (!isParentTaskTemplate) {
    const isArchiveVisible = !visibleItems || visibleItems.includes('archive')

    isArchiveVisible &&
      menuItems.push({
        id: 'archive',
        icon: task.archivedAt ? 'IconArchiveOff' : 'IconArchive',
        title: task.archivedAt ? Lang.msg.menuUnarchive : Lang.msg.menuArchive,
        onClick: toggleArchive,
        disabled: !archiveAllowed(hub.id)
      })

    const isDuplicateVisible =
      !visibleItems || visibleItems.includes('duplicate')
    isDuplicateVisible &&
      duplicate &&
      menuItems.push({
        id: 'duplicate',
        icon: 'IconCopy',
        title: Lang.msg.duplicate,
        onClick: () => Task.duplicate(hub)
      })

    const isCopyLinkVisible = !visibleItems || visibleItems.includes('link')
    isCopyLinkVisible &&
      menuItems.push({
        id: 'link',
        icon: 'IconLink',
        title: Lang.msg.chat.copyLinkOnTask,
        onClick: copyLink
      })

    const isSelectMultipleVisible =
      !visibleItems || visibleItems.includes('select-multiple')
    isSelectMultipleVisible &&
      canSelectMultiple &&
      board &&
      menuItems.push({
        id: 'select-multiple',
        icon: 'IconSquareCheck',
        title: Lang.msg.multiActionsWithTasks.selectMultipleTasks,
        onClick: selectMultipleTasksToggleOn
      })

    const isUploadCoverVisible =
      !visibleItems || visibleItems.includes('upload-cover')
    if (isUploadCoverVisible) {
      menuItems.push({
        id: 'upload-cover',
        icon: 'IconPhoto',
        title: Lang.msg.uploadTaskImage,
        onClick: uploadCover
      })
      if (['manual', 'auto'].includes(Task.getTaskCoverType(hub))) {
        menuItems.push({
          id: 'remove-cover',
          icon: 'IconPhotoOff',
          title: Lang.msg.removeTaskImage,
          onClick: removeCover
        })
      }
    }

    const isRemoveFromBoardVisible =
      !visibleItems || visibleItems.includes('remove-from-board')
    isRemoveFromBoardVisible &&
      removeFromBoard &&
      menuItems.push({
        id: 'remove-from-board',
        icon: 'IconCustomRemoveFromBoard',
        title: Lang.msg.subtask.removeFromBoard,
        onClick: () => Column.removeFromBoard(hub)
      })
  }

  const isTaskColorVisible =
    !visibleItems || visibleItems.includes('task-colorpicker')
  const canColoredTask = PermissionsChecker.checkEditTaskStickers(
    hub,
    'not-user',
    undefined
  )
  const canBeShowed = hub.location !== noBoardContainerId && !asSubtask
  isTaskColorVisible &&
    canBeShowed &&
    canColoredTask &&
    menuItems.push(
      {separator: true},
      {
        id: 'task-colorpicker',
        element: <TaskColorPicker task={hub} closeMenu={closeMenu} />
      }
    )
  isTaskColorVisible && canColoredTask && menuItems.push({separator: true})

  const isDeleteVisible = !visibleItems || visibleItems.includes('delete')
  isDeleteVisible &&
    menuItems.push({
      id: 'delete',
      icon: 'IconTrash',
      title: Lang.msg.delete,
      onClick: deleteTask,
      disabled: !isParentTaskTemplate && !deleteAllowed(hub.id)
    })

  const isRemoveFromSubtasksVisible =
    !visibleItems || visibleItems.includes('remove-from-subtasks')
  if (
    isRemoveFromSubtasksVisible &&
    !isParentTaskTemplate &&
    removeFromSubtasks &&
    !isWithoutBoard
  ) {
    menuItems.push({
      id: 'remove-from-subtasks',
      icon: 'IconUnlink',
      title: Lang.msg.subtask.removeFromSubtasks,
      onClick: removeFromSubtasks
    })
  }

  return (
    <Dropdown
      reference={reference}
      hideArrow
      content={<Menu items={menuItems} onItemClicked={closeMenu} />}
      visible
      onVisibleChange={closeMenu}
      theme="new-menu"
    />
  )
}

interface AddMovementProps {
  menuItems: MenuItemData[]
  moveOpen: BooleanHookState
  isWithoutBoard: boolean
  task: Hub
  board?: Hub
  canMove?: boolean
}

const addMovement = (props: AddMovementProps): void => {
  const {menuItems, moveOpen, isWithoutBoard, task, board, canMove} = props
  if (!task) {
    return
  }

  const canChangeBoardSettings =
    board && PermissionsChecker.checkBoardSettings(board)

  const movementList =
    isWithoutBoard || !board
      ? []
      : MovementClient.listTemplatesInColumnEnabled(board.id, task.location)

  const moveItem: MenuItemData = {
    id: 'move-task',
    icon: 'IconCornerUpRight',
    title: Lang.msg.move,
    items: [
      {
        id: 'move-task-specify-destination',
        icon: 'IconCornerUpRight',
        title: Lang.msg.specifyDestination,
        onClick: moveOpen.turnOn
      }
    ],
    disabled: !canMove
  }

  if (MovementClient.hasFeature()) {
    canChangeBoardSettings &&
      board &&
      moveItem.items?.push({
        id: 'move-task-create-rule',
        icon: 'IconCustomTemplate',
        title: Lang.msg.createTaskMovementRule,
        onClick: () => {
          boardSettingsStore.setCurrentTabId('movementTemplates', 'create')
          boardSettingsStore.toggle()
          void Tracking.send(
            TrackingEvent.boardSettingsOpenMovementTemplatesCreateViaCardMenu
          )
        }
      })

    canChangeBoardSettings &&
      board &&
      moveItem.items?.push({
        id: 'move-task-get-list',
        icon: 'IconCustomTemplate',
        title: Lang.msg.movementTemplates.settingsTemplate,
        onClick: () => {
          boardSettingsStore.setCurrentTabId('movementTemplates', 'list')
          boardSettingsStore.toggle()
          void Tracking.send(
            TrackingEvent.boardSettingsOpenMovementTemplatesViaCardMenu
          )
        }
      })

    if (!isMirroredHub(task) && movementList.length) {
      moveItem.items?.push({separator: true, empty: true})

      moveItem.items?.push({separator: true, empty: true})
      moveItem.items?.push(
        ...movementList.map(template => ({
          id: `move-task-${template.id}`,
          icon: 'IconCornerUpRight' as const,
          title: template.data.movementTemplate?.title || '',
          onClick: () => {
            moveTemplateApply(task.id, template)
          }
        }))
      )
    }
  }

  menuItems.push(moveItem)
}
